import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Newspaper,
  AppWindow,
  CreditCard,
  LogOut,
  Menu,
  X,
  Brain,
  Shield,
  Briefcase
} from 'lucide-react';
import { logout } from '../utils/api';
import { toast } from 'sonner';

export const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada correctamente');
    navigate('/admin');
  };
  
  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/news', icon: Newspaper, label: 'Noticias' },
    { path: '/admin/apps', icon: AppWindow, label: 'Apps' },
    { path: '/admin/plans', icon: CreditCard, label: 'Planes' },
    { path: '/admin/legal', icon: Shield, label: 'Legal' },
    { path: '/admin/services', icon: Briefcase, label: 'Servicios' },
    { path: '/admin/payments', icon: CreditCard, label: 'Pagos' },
  ];
  
  return (
    <div className="min-h-screen bg-black selection:bg-[#C6FF00] selection:text-black">
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5 p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3 group">
          <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center border border-white/10 group-hover:border-[#C6FF00]/50 transition-colors">
            <Brain size={18} className="text-white group-hover:text-[#C6FF00] transition-colors" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-black tracking-tighter leading-none text-white">BUILD</span>
            <span className="text-[8px] font-black tracking-[0.4em] text-[#C6FF00] leading-none mt-1">BRAINS</span>
          </div>
        </Link>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="text-white/60 hover:text-white transition-colors"
          data-testid="mobile-sidebar-toggle"
        >
          {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-black border-r border-white/5 z-40 transition-transform duration-500 ease-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
        data-testid="admin-sidebar"
      >
        <div className="p-8 border-b border-white/5">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 group-hover:border-[#C6FF00]/50 transition-colors shadow-2xl shadow-black">
              <Brain size={20} className="text-white group-hover:text-[#C6FF00] transition-colors" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-black tracking-tighter leading-none text-white">BUILD</span>
              <span className="text-[10px] font-black tracking-[0.4em] text-[#C6FF00] leading-none mt-1">BRAINS</span>
            </div>
          </Link>
          <div className="mt-6 flex items-center space-x-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C6FF00] animate-pulse"></div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Nexus Control</p>
          </div>
        </div>
        
        <nav className="p-4 py-8 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsSidebarOpen(false)}
              className={`flex items-center space-x-4 px-6 py-4 rounded-2xl transition-all duration-300 group ${
                location.pathname === item.path
                  ? 'bg-[#C6FF00]/10 text-[#C6FF00] border border-[#C6FF00]/20'
                  : 'text-white/40 hover:bg-white/[0.02] hover:text-white border border-transparent'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={18} className={`${location.pathname === item.path ? 'text-[#C6FF00]' : 'group-hover:text-white transition-colors'}`} />
              <span className="text-xs font-black uppercase tracking-widest">{item.label}</span>
            </Link>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/5">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-4 px-6 py-4 rounded-2xl text-white/30 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20 border border-transparent transition-all duration-300"
            data-testid="logout-button"
          >
            <LogOut size={18} />
            <span className="text-xs font-black uppercase tracking-widest">Terminar Sesión</span>
          </button>
        </div>
      </aside>
      
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0 bg-black">
        <div className="p-8 md:p-12 relative">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C6FF00]/5 blur-[150px] pointer-events-none opacity-50"></div>
          <div className="relative z-10">
            {children}
          </div>
        </div>
      </main>
      
      {isSidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};
